import React, { useEffect, useState } from "react";
import { Alert, Snackbar } from "@mui/material";
import { useAuth } from "../../context/AuthContext";
import {
    getInstitutionBranches
} from "../../services/branch";
import {
    getInstitutionEmployee,
    inviteEmployee,
    deleteEmployee,
    editEmployee
} from "../../services/employee";
import {
    assignEmployeeServices,
    getEmployeeServices,
    removeEmployeeService
} from "../../services/employee-services";

import Table from "../../components/Table/Table";
import ConfirmationModal from "../../components/Modals/ConfirmationModal/ConfirmationModal";
import EmployeeServicesModal from "../../components/Modals/EmployeeServicesModal/EmployeeServicesModal";

export default function ManageEmployees() {
    const { token, institution } = useAuth();
    const [branches, setBranches] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [editId, setEditId] = useState(null);
    const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
    const [employeeToDelete, setEmployeeToDelete] = useState(null);
    const [confirmationMsg, setConfirmationMsg] = useState("هل أنت متأكد أنك تريد حذف هذا الموظف؟");
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarContent, setSnackbarContent] = useState({ message: "", type: "" });
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [showServiceModal, setShowServiceModal] = useState(false);

    const handleSnackbar = (message, type = "success") => {
        setSnackbarContent({ message, type });
        setSnackbarOpen(true);
    };

    useEffect(() => {
        loadData();
    }, [token]);

    const loadData = async () => {
        if (!token) return;
        setIsLoading(true);

        try {
            const [branchRes, empRes] = await Promise.all([
                getInstitutionBranches(institution.id),
                getInstitutionEmployee(token),
            ]);

            setBranches(branchRes.data || []);

            const enriched = await Promise.all(
                empRes.data.map(async (emp) => {
                    const serviceRes = await getEmployeeServices(emp.id);
                    const branch = branchRes.data.find(b => b.id === emp.branchId);
                    return {
                        id: emp.id,
                        name: emp.name || "",
                        email: emp.email || "",
                        role: (emp.roles?.[0] === "branch-manager") ? "مدير فرع" : "موظف",
                        branchId: emp.branchId || "",
                        branchName: branch?.name || "",
                        status: emp.registered ? "مسجل" : "تمت دعوته",
                        createdAt: new Date(emp.createdAt).toLocaleDateString("ar-EG"),
                        isActive: emp.registered,
                        services: serviceRes?.data || [],
                    };
                })
            );

            setEmployees(enriched);
        } catch (err) {
            console.error("Failed to load data:", err);
        } finally {
            setIsLoading(false);
        }
    };

    const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

    const onAdd = (setRecentlyAddedId) => {
        if (editId) return;
        const newId = "temp-" + Date.now();
        setEmployees(prev => [{
            id: newId,
            email: "",
            role: "موظف",
            branchId: "",
            branchName: "",
            status: "جديد",
            services: [],
        }, ...prev]);
        setEditId(newId);
        setRecentlyAddedId(newId);
    };

    const onSave = async (id, newData) => {
        const isNew = id.toString().startsWith("temp-");

        if (!newData.email || !newData.branchId) {
            handleSnackbar("يرجى تعبئة البريد الإلكتروني واختيار الفرع", "error");
            return;
        }

        if (!isValidEmail(newData.email)) {
            handleSnackbar("صيغة البريد الإلكتروني غير صحيحة", "error");
            return;
        }

        try {
            if (isNew) {
                setIsLoading(true);
                const res = await inviteEmployee(token, {
                    email: newData.email,
                    branchId: newData.branchId,
                    role: "موظف",
                });

                const userId = res?.data?.userId;
                const branch = branches.find(b => b.id === newData.branchId);

                const newEmployee = {
                    ...newData,
                    id: userId,
                    role: "موظف",
                    status: "تمت دعوته",
                    branchName: branch?.name || "",
                    createdAt: new Date().toLocaleDateString("ar-EG"),
                    isActive: false,
                };

                setEmployees(prev =>
                    prev.map(emp => emp.id === id ? newEmployee : emp)
                );

                handleSnackbar("تم إرسال الدعوة بنجاح");
                setEditId(null);
            } else {
                const branch = branches.find(b => b.id === newData.branchId);
                const oldEmployee = employees.find(emp => emp.id === id);
                const oldServiceIds = (oldEmployee.services || []).map(s => s.id);
                const newServiceIds = (newData.services || []).map(s => s.id);

                await editEmployee(token, id, {
                    name: newData.name,
                    branchId: newData.branchId
                });

                await assignEmployeeServices(id, newServiceIds, token);

                for (const serviceId of oldServiceIds.filter(id => !newServiceIds.includes(id))) {
                    await removeEmployeeService(id, serviceId, token);
                }

                setEmployees(prev =>
                    prev.map(emp => {
                        if (emp.id !== id) return emp;
                        const selectedBranch = branches.find(b => b.id === Number(newData.branchId));
                        return {
                            ...emp,
                            ...newData,
                            branchName: selectedBranch?.name || emp.branchName,
                        };
                    })
                );

                handleSnackbar("تم تحديث الموظف بنجاح");
                setEditId(null);
            }
        } catch (err) {
            handleSnackbar(err.response?.data || "فشل في إرسال الدعوة", "error");
            if (isNew) {
                setEmployees(prev => prev.filter(emp => emp.id !== id));
            }
        } finally {
            setIsLoading(false);
        }
    };

    const onDelete = async () => {
        setIsLoading(true);
        setConfirmationModalOpen(false);
        try {
            await deleteEmployee(token, employeeToDelete);
            setEmployees(prev => prev.filter(e => e.id !== employeeToDelete));
            handleSnackbar("تم حذف الموظف بنجاح");
        } catch (err) {
            handleSnackbar("خطأ أثناء الحذف", "error");
        } finally {
            setIsLoading(false);
        }
    };

    const columns = [
    { key: "name", label: "الاسم" },
    {
        key: "email",
        label: "البريد الإلكتروني",
        editableIf: (row) => row.id?.toString().startsWith("temp-")
    },
    {
        key: "branchId",
        label: "الفرع",
        options: branches.map((b) => ({ value: b.id, label: b.name })),
        isDropdown: true,
        render: (row) => row.branchName,
    },
    { key: "role", label: "الدور" },
    { key: "status", label: "الحالة" },
    { key: "createdAt", label: "تاريخ الإضافة" },
];


    return (
        <div className="page">
            <div className="content-view">
                <h1 className="text-xl font-bold mb-4">إدارة الموظفين</h1>

                <Table
                    detailsColumnLabel="الخدمات"
                    data={employees}
                    columns={columns}
                    loading={isLoading}
                    onAdd={onAdd}
                    onSave={onSave}
                    onDelete={(id) => {
                        const emp = employees.find(e => e.id === id);
                        setConfirmationMsg(emp?.isActive ? "هل أنت متأكد أنك تريد حذف هذا الموظف؟" : "هل أنت متأكد أنك تريد إلغاء الدعوة؟");
                        setEmployeeToDelete(id);
                        setConfirmationModalOpen(true);
                    }}
                    onEdit={(row) => {
                        setSelectedEmployee(row);
                        setShowServiceModal(true);
                    }}
                    editId={editId}
                    setEditId={setEditId}
                    editableColumns={["branchId", "services", "email"]}
                    tagColumn="status"
                    highlightConfig={{
                        status: {
                            "مسجل": "#01c8aa",
                            "تمت دعوته": "#facc15",
                            "جديد": "#f59e0b"
                        },
                    }}
                />

                {showServiceModal && selectedEmployee && (
                    <EmployeeServicesModal
                        employee={selectedEmployee}
                        setShowModal={setShowServiceModal}
                        branches={branches}
                    />
                )}

                {confirmationModalOpen && (
                    <ConfirmationModal
                        message={confirmationMsg}
                        handleConfirm={onDelete}
                        onClose={() => setConfirmationModalOpen(false)}
                    />
                )}

                <Snackbar
                    open={snackbarOpen}
                    autoHideDuration={6000}
                    onClose={() => setSnackbarOpen(false)}
                >
                    <Alert
                        onClose={() => setSnackbarOpen(false)}
                        severity={snackbarContent.type}
                        variant="filled"
                        sx={{ width: "100%" }}
                    >
                        {snackbarContent.message}
                    </Alert>
                </Snackbar>
            </div>
        </div>
    );
}
